import { gql, useMutation } from '@apollo/client'
import { getCookie } from '@jusbrasil-web/shared-utils-cookies'
import { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import {
  UseDocumentFeatureMutation,
  UseDocumentFeatureMutationMutation,
  UseDocumentFeatureMutationMutationVariables,
} from '../../helpers'
import {
  ViewLockContext_DocumentFragment,
  ViewLockContext_ViewerFragment,
} from './__generated__/view-lock-context.graphql'
import {
  ACTIVE_CAMPAIGNS,
  EXCLUDED_ARTIFACTS_FROM_PPC_LOCK,
  LockType,
  viewContentFeatureCode,
} from './constants'

export interface ViewLockContextProps {
  children: ReactNode
  viewer: ViewLockContext_ViewerFragment | null
  document: ViewLockContext_DocumentFragment | null
  featureContext: {
    featureCode: string
    canUseFeature: boolean
  }
  enableLawyerLock?: boolean
  enableOabLock?: boolean
  enableJournalLock?: boolean
  enableEnrichedContentLock?: boolean
}

const Context = createContext({
  isLocked: false,
  lockType: LockType.UNLOGGED,
  isFreeUsage: true,
  featureCode: viewContentFeatureCode,
  hasPaidCampaign: false,
})

export function useViewLockContext() {
  return useContext(Context)
}

function checkPaidCampaign(document: ViewLockContext_DocumentFragment | null) {
  const artifact = document && document.artifact
  const campaign = getCookie(null, 'ads_utm_campaign')
  const hasPaidCampaign =
    artifact &&
    ACTIVE_CAMPAIGNS.includes(campaign) &&
    !(EXCLUDED_ARTIFACTS_FROM_PPC_LOCK as readonly string[]).includes(artifact)

  return Boolean(hasPaidCampaign)
}

function getLockType(
  viewer: ViewLockContext_ViewerFragment | null,
  { enableLawyerLock = false, enableOabLock = false, enableJournalLock = false }
) {
  if (!viewer || enableJournalLock) {
    return LockType.UNLOGGED
  }
  if (enableLawyerLock) {
    if (!viewer.isLawyer) {
      return LockType.LAWYER
    }
    if (enableOabLock && !viewer.isVerified) {
      return LockType.OAB
    }
  }

  return LockType.LOGGED
}

function shouldLock({
  viewer,
  document,
  enableLawyerLock,
  enableOabLock,
  enableJournalLock,
  enableEnrichedContentLock,
  canUseFeature,
}: {
  viewer: ViewLockContext_ViewerFragment | null
  document: ViewLockContext_DocumentFragment | null
  enableLawyerLock: boolean
  enableOabLock: boolean
  enableJournalLock: boolean
  enableEnrichedContentLock: boolean
  canUseFeature: boolean
}) {
  if (!!document && canUseFeature) return false

  return (
    !!document &&
    (!document.canViewFullDocument ||
      (enableLawyerLock && (!viewer || !viewer.isLawyer)) ||
      (enableOabLock && (!viewer || !viewer.isLawyer || !viewer.isVerified)) ||
      enableJournalLock ||
      enableEnrichedContentLock)
  )
}

export const ViewLockContext = (props: ViewLockContextProps) => {
  const {
    viewer,
    document,
    children,
    featureContext,
    enableLawyerLock = false,
    enableOabLock = false,
    enableJournalLock = false,
    enableEnrichedContentLock = false,
  } = props

  const isLogged = viewer
  const isFreeUsage = viewer ? !viewer.hasLegalResearch : true
  const hasDocument = !!document
  const hasAuthor = document && Boolean(document.publisher)
  const isUserAnAuthor =
    hasAuthor && isLogged && document.publisher && document.publisher.pid === viewer.pid
  const isOverdue =
    viewer && viewer.subscriptions && viewer.subscriptions.some((s) => s && s.overdue)
  const linkedAccount = viewer?.boxProfileInfo?.linkedAccount ?? false
  const isCustomPlanWithLegalResearch =
    viewer &&
    viewer.subscriptions &&
    viewer.subscriptions.some((s) => s && s.plan?.customPlan) &&
    viewer.hasLegalResearch
  const isCustomPlanWithLegalResearchOrLinkedAccount =
    isCustomPlanWithLegalResearch || linkedAccount
  const hasPaidCampaign = checkPaidCampaign(document)
  const { featureCode, canUseFeature } = featureContext

  const lockType = getLockType(viewer, {
    enableLawyerLock,
    enableOabLock,
    enableJournalLock,
  })
  const [isLocked, setIsLocked] = useState(
    shouldLock({
      viewer,
      document,
      enableLawyerLock,
      enableOabLock,
      enableJournalLock,
      enableEnrichedContentLock,
      canUseFeature,
    })
  )
  const [isLoading, setIsLoading] = useState(true)

  const [consumeDocviewFeature] = useMutation<
    UseDocumentFeatureMutationMutation,
    UseDocumentFeatureMutationMutationVariables
  >(UseDocumentFeatureMutation)

  useEffect(() => {
    if (isUserAnAuthor) {
      setIsLocked(false)
      return
    }

    if ((!isLocked && isFreeUsage && hasPaidCampaign) || isOverdue) {
      setIsLocked(true)
      setIsLoading(false)
      return
    }

    setIsLocked(
      shouldLock({
        viewer,
        document,
        enableLawyerLock,
        enableOabLock,
        enableJournalLock,
        enableEnrichedContentLock,
        canUseFeature,
      })
    )
    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewer,
    document,
    isFreeUsage,
    enableLawyerLock,
    enableOabLock,
    enableJournalLock,
    enableEnrichedContentLock,
    hasPaidCampaign,
  ])

  useEffect(() => {
    async function maybeTriggerOnShow() {
      if (hasDocument && !isCustomPlanWithLegalResearchOrLinkedAccount && !isLocked && !isLoading) {
        consumeDocviewFeature({
          variables: {
            input: {
              docId: document.docId,
              artifact: document.artifact,
              feature: featureCode,
            },
          },
          onError: (error) => console.warn(error),
        })
      }
    }

    maybeTriggerOnShow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDocument, isCustomPlanWithLegalResearchOrLinkedAccount, isLocked, isLoading])

  return (
    <Context.Provider
      value={{
        isLocked,
        lockType,
        isFreeUsage,
        featureCode,
        hasPaidCampaign,
      }}
    >
      <>{children}</>
    </Context.Provider>
  )
}

ViewLockContext.fragments = {
  viewer: gql`
    fragment ViewLockContext_viewer on Profile {
      pid
      hasLegalResearch
      boxProfileInfo {
        linkedAccount
      }
      subscriptions {
        overdue
        plan {
          customPlan
        }
      }
      isLawyer
      isVerified
    }
  `,
  document: gql`
    fragment ViewLockContext_document on Document {
      docId
      artifact
      publisher {
        pid
      }
      canViewFullDocument: viewerCanAccessFullDocument
    }
  `,
}
